import { useState, useEffect, useCallback, type ReactNode } from 'react';
import api from '../api/client';
import { AuthContext } from './AuthContext';
import { subscribeToPushNotifications, syncPushSubscription } from '../lib/pushNotifications';
import type { User } from '../types';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('token');
  });
  const [isLoading, setIsLoading] = useState(true);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post('/api/v1/auth/login', { email, password });
    const newToken = res.data.token;
    const newUser = res.data.user ?? res.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(newUser);

    // Kullanıcı login olduktan sonra push bildirim izni iste.
    // Daha önce izin verilmemişse (default) tek seferlik modal gösterilir.
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      subscribeToPushNotifications().catch(() => {
        // Kullanıcı reddederse veya desteklenmiyorsa sessizce geç.
      });
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/v1/auth/logout');
    } catch {
      // ignore
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  const fetchUser = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/api/v1/auth/me');
      setUser(res.data.user ?? res.data);
    } catch (err: unknown) {
      const e = err as { response?: { status?: number } };
      if (e.response?.status === 401) {
        logout();
      }
    } finally {
      setIsLoading(false);
    }
  }, [logout]);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.get('/api/v1/auth/me');
      setUser(res.data.user ?? res.data);
    } catch {
      // ignore; auth interceptors handle 401
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUser().then(() => {
        // Kullanıcı daha önce izin verdiyse sayfa yenilendiğinde subscription'ı
        // backend ile senkronize et (pushsubscriptionchange sonrası yeniden abone
        // olma senaryoları için).
        if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
          syncPushSubscription().catch(() => {});
        }
      });
    } else {
      setUser(null);
      setIsLoading(false);
    }
  }, [token, fetchUser]);

  // Service worker'dan gelen push subscription değişikliği mesajlarını dinle.
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGE') {
        if (Notification.permission === 'granted') {
          syncPushSubscription().catch(() => {});
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, refreshUser, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}
